import axios, {AxiosError, AxiosResponse} from 'axios';

export type HttpHeaders = {[key: string]: string};

const httpContentTypeKey = 'Content-type';
const httpJsonContentType = 'application/json; charset=UTF-8';
const httpFormContentType = 'multipart/form-data; charset=UTF-8';
export const httpHeaderWithFormContentType: HttpHeaders = {[httpContentTypeKey]: httpFormContentType};

const hasFile = (body?: object) => {
	if (!body) return false;

	return Object.values(body).reduce((prev, curr) => prev || curr instanceof File, false);
};
const shouldBeFormData = (body: object, headers: HttpHeaders) => {
	if (headers && httpContentTypeKey in headers && headers[httpContentTypeKey] === httpFormContentType) return true;

	return hasFile(body);
};

// const addDefaultHeaders = (header: HttpHeaders, body?: object) => {
// 	const headers = {...header};
// 	headers['Content-type'] = hasFile(body) ? 'multipart/form-data' : 'application/json';
// 	headers['Content-type'] = `${headers['Content-type']}; charset=UTF-8`;
// 	return headers;
// };

const addDefaultHeaders = (header: HttpHeaders, body?: object) => {
	const headers = {...header};
	if (!(httpContentTypeKey in headers)) {
		headers[httpContentTypeKey] = hasFile(body) ? httpFormContentType : httpJsonContentType;
	}
	return headers;
};

const buildPostData = (body: object, headers: HttpHeaders) => {
	if (!shouldBeFormData(body, headers)) return body;

	const data = new FormData();
	Object.entries(body).forEach(([key, value]) => {
		if (Array.isArray(value)) {
			value.forEach((v, i) => data.append(`${key}[${i}]`, v));
		} else {
			data.append(key, value);
		}
	});
	return data;
};

export function addAuthHeader(headers: HttpHeaders | undefined, token: string | undefined): HttpHeaders {
	const headersWithAuth = {...(headers ?? {})};
	headersWithAuth.Authorization = `Bearer ${token ?? ''}`;
	return headersWithAuth;
}

export async function getHttp<T>(uri: string, header: HttpHeaders = {}): Promise<T> {
	const res = await axios.get<T>(uri, {
		headers: addDefaultHeaders(header),
	});
	return res.data;
}

export async function postHttp<T>(uri: string, body: object, header: HttpHeaders = {}): Promise<T> {
	const headers = addDefaultHeaders(header, body);

	const res = await axios.post<T>(uri, buildPostData(body, headers), {
		headers,
	});
	return res.data;
}

export async function deleteHttp<T>(uri: string, header: HttpHeaders = {}): Promise<T> {
	const res = await axios.delete<T>(uri, {
		headers: addDefaultHeaders(header),
	});
	return res.data;
}

export async function putHttp<T>(uri: string, body: object, header: HttpHeaders = {}): Promise<T> {
	const res = await axios.put<T>(uri, body, {
		headers: addDefaultHeaders(header),
	});
	return res.data;
}

export class HttpError extends Error {
	constructor(code: number, msg: string) {
		super(msg);

		Object.setPrototypeOf(this, HttpError.prototype);
	}
}

export function isHttpError(error: unknown) {
	return typeof error === 'object' && error && 'response' in error;
}

export function buildHttpError(error: unknown) {
	const e = error as AxiosError;
	const code = e.response ? e.response.status : 500;
	const res = e.response?.data;
	const message = typeof res === 'object' && res && 'message' in res ? res.message : e.message;
	return new HttpError(code, message as string);
}

export function isUnauthorizedResponse(response: AxiosResponse | undefined) {
	return response?.status === 403;
}

export function isUnauthorizedHttpError(error: unknown) {
	if (!isHttpError(error)) return false;

	return isUnauthorizedResponse((error as AxiosError)?.response);
}
